import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { CreateAgencyModal } from '@frontend/features/agencies/components/CreateAgency';
import { useGetAllAgencies } from '@frontend/features/agencies/queries';
import { usePagination, Table } from '@frontend/features/common/components/Table';
import { EditAgencyModal } from '@frontend/features/agencies/components/EditAgency/EditAgencyModal';
import { Link } from '@frontend/features/common/components/Link';
import { ListLayout } from '@frontend/features/common/components/ListLayout';

import { routes } from '../routes';

export const AgencyList = () => {
  const { t } = useTranslation();
  const pagination = usePagination();
  const theme = useTheme();
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<number | undefined>();
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);

  const handleOpenEditModal = (id: number) => {
    setEditId(id);
    setIsOpenEdit(true);
  };

  const handleCloseEditModal = () => {
    setEditId(undefined);
    setIsOpenEdit(false);
  };

  const toggleCreateModal = () => setIsOpenCreate((prevState) => !prevState);

  const { data, isLoading } = useGetAllAgencies({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
  });

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <ListLayout title={t('agencies.title')} subtitle={t('agencies.subtitle')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.count || 0} {t('clients.results')}
        </Typography>

        <SearchInput placeholder="Buscar" onSearch={handleOnSearch} />
        <ToggleButtonGroup
          exclusive
          sx={{
            height: theme.spacing(5),
            '& button': {
              padding: theme.spacing(1),
              width: theme.spacing(5),
            },
          }}
        >
          <ToggleButton onClick={toggleCreateModal} value="add" aria-label="right aligned">
            <AddIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <CreateAgencyModal open={isOpenCreate} onClose={toggleCreateModal} />
      <EditAgencyModal agencyId={editId} open={isOpenEdit} onClose={handleCloseEditModal} />

      {data?.count !== undefined && data.count > 0 && (
        <Table
          columns={[
            {
              id: 'name',
              label: t('agencies.table.name'),
              align: 'left',
            },
            {
              id: 'country',
              label: t('agencies.table.country'),
              align: 'left',
            },
            { id: 'currency', label: t('agencies.table.currency'), align: 'left' },
            {
              id: 'isPrivate',
              label: t('agencies.table.isPrivate'),
              align: 'left',
              format: (value) => <>{value ? t('common.yes') : t('common.no')}</>,
            },
            { id: 'actions', label: '', align: 'left' },
          ]}
          data={{
            key: 'id',
            items: data.items.map((item) => ({
              ...item,
              name: <Link to={routes.dashboard.agencyView.root.replace(':id', String(item.id))}>{item.name}</Link>,
              actions: (
                <Button onClick={() => handleOpenEditModal(item.id)}>
                  <EditIcon color="primary" />
                </Button>
              ),
            })),
            totalCount: data.count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}
      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </ListLayout>
  );
};
